import { indexFields } from './conditions.js';
import type { AnswerMap, FieldDef, RepeaterValue, SectionDef, TemplateDefinition } from './types.js';

/** Removes internal-audience fields (and internal repeater children) for client viewers. */
export function clientSafeDefinition(def: TemplateDefinition): TemplateDefinition {
  const stripField = (f: FieldDef): FieldDef | null => {
    if (f.audience === 'internal') return null;
    if (f.children) return { ...f, children: f.children.map(stripField).filter((x): x is FieldDef => !!x) };
    return f;
  };
  return {
    ...def,
    sections: def.sections.map((s): SectionDef => ({ ...s, fields: s.fields.map(stripField).filter((x): x is FieldDef => !!x) })),
  };
}

/** Ids of internal fields, including every child of an internal repeater. */
export function internalFieldIds(def: Pick<TemplateDefinition, 'sections'>): Set<string> {
  const ids = new Set<string>();
  for (const e of indexFields(def).values()) {
    if (e.field.audience === 'internal' || e.parent?.audience === 'internal') ids.add(e.field.id);
  }
  return ids;
}

/** Drops answers to internal fields (including internal children inside repeater rows). */
export function clientSafeAnswers(def: Pick<TemplateDefinition, 'sections'>, answers: AnswerMap): AnswerMap {
  const hidden = internalFieldIds(def);
  const out: AnswerMap = {};
  for (const [k, v] of Object.entries(answers)) {
    if (hidden.has(k)) continue;
    if (v.kind === 'value' && v.value && typeof v.value === 'object' && 'rows' in (v.value as object)) {
      const rows = (v.value as RepeaterValue).rows.map((r) => ({
        ...r,
        values: Object.fromEntries(Object.entries(r.values).filter(([ck]) => !hidden.has(ck))),
      }));
      out[k] = { kind: 'value', value: { rows } };
    } else out[k] = v;
  }
  return out;
}
